/**
 * @module utils/dispatcher
 */

/**
 * @typedef {module:utils/object.Model} Model
 * @typedef {*} Value
 */
import array from './array.js'
import object from './object.js'
import resolvable from './resolvable.js'

const empty = array([])
const resolvers = new WeakMap()

const subscribe = async function * (
  entries
) {
  try {
    while (true) {
      if (!entries.length) {
        entries.push(resolvable())
      }
      
      const [[promise]] = entries
      
      yield promise
      entries.shift()
    }
  } finally {
    entries.length = 0
  }
}

/**
 * @typedef {function} Dispatch
 * @param {Value} value
 */

/**
 * @typedef {AsyncGenerator<Value>} Subscribe
 */

/**
 * @typedef {{}} Dispatcher
 * @property dispatch
 * @property {Subscribe} subscribe
 */
export default object(null, {
  /**
   * Dispatches a provided value on the current dispatcher
   * @param {Value} value
   */
  dispatch (
    value
  ) {
    for (const entries of resolvers.get(this) ?? empty) {
      const resolver = entries.at(-1)
      
      if (resolver) {
        const [, { resolve }] = resolver
        
        entries.push(resolvable())
        resolve(value)
      }
    }
  },
  /**
   * Returns an asynchronous generator yielding the dispatched values
   * @returns {AsyncGenerator<Value>}
   * @throws {ReferenceError}
   */
  subscribe () {
    if (Object.getPrototypeOf(this) === null) {
      throw new ReferenceError('Dispatcher model used as instance')
    }
    
    if (!resolvers.has(this)) {
      resolvers.set(this, new Set())
    }
    
    const entries = [resolvable()]
    const subscription = subscribe(entries)
    
    resolvers.get(this).add(entries)
    
    return subscription
  }
})
